import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import type { PlayProps } from "@/types/puzzle";

export default function MultipleChoicePlay({ data, onAnswer, result }: PlayProps) {
  const [selected, setSelected] = useState<string | null>(null);

  const question = (data.question as string) ?? "";
  const options = (data.options as string[]) ?? [];

  const answered = result !== null;
  const solutionOptions = (result?.solution.options as string[]) ?? [];
  const correctIndex = (result?.solution.correctIndex as number) ?? -1;
  const correctOption = solutionOptions[correctIndex] ?? null;

  const handleSelect = (option: string) => {
    if (answered) return;
    setSelected(option);
  };

  const handleCheck = () => {
    if (!selected || answered) return;
    onAnswer(selected);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-display font-semibold text-foreground text-center animate-fade-in-up">
        {question}
      </h2>
      <div className="grid gap-3 max-w-lg mx-auto">
        {options.map((option, i) => {
          const isSelected = option === selected;
          const isCorrectOption = answered && option === correctOption;
          const isWrongPick = answered && isSelected && option !== correctOption;

          let classes = "bg-card border-border text-foreground hover:bg-secondary/50";

          if (answered) {
            if (isCorrectOption) {
              classes = "bg-green-500/15 border-green-500 text-green-700";
            } else if (isWrongPick) {
              classes = "bg-red-500/15 border-red-500 text-red-700 animate-shake";
            } else {
              classes = "bg-card border-border text-muted-foreground opacity-50";
            }
          } else if (isSelected) {
            classes = "bg-foreground/5 border-foreground text-foreground";
          }

          return (
            <button
              key={i}
              onClick={() => handleSelect(option)}
              disabled={answered}
              className={`w-full flex items-center rounded-lg border-2 border-b-4 p-4 text-left text-base font-medium transition-all active:scale-[0.98] enabled:hover:-translate-y-0.5 ${classes}`}
              style={{ animationDelay: `${i * 50}ms`, animationFillMode: "backwards" }}
            >
              <span
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold mr-3 shrink-0 transition-colors ${
                  isCorrectOption
                    ? "bg-green-500 text-white"
                    : isWrongPick
                      ? "bg-red-500 text-white"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                {isCorrectOption ? (
                  <Check className="w-4 h-4" strokeWidth={3} />
                ) : isWrongPick ? (
                  <X className="w-4 h-4" strokeWidth={3} />
                ) : (
                  String.fromCharCode(65 + i)
                )}
              </span>
              <span className="flex-1">{option}</span>
            </button>
          );
        })}
      </div>

      {/* Always reserve the Check button's height so hiding it after answering
          doesn't shrink the centered content and shift the question downward. */}
      <div className="max-w-lg mx-auto h-12">
        {!answered && (
          <Button
            onClick={handleCheck}
            disabled={!selected}
            className="w-full h-12 text-base font-bold"
          >
            Check
          </Button>
        )}
      </div>
    </div>
  );
}
